import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled, useTheme } from '@mui/material';
import { IconBolt } from '@tabler/icons-react';

import { Typography } from '@zendesk/forethought-elements';
import { CustomEventTranscriptComponent } from './types';
import { ColorMode } from 'src/components/app/types';
import { useGetEventConfigurationsQuery } from 'src/services/custom-events';
import { CustomEventPreviewLogProps } from 'src/slices/workflow-preview/types';

export const CustomEvent = ({
  theme = 'dark',
  transcriptComponent,
}: {
  theme?: ColorMode;
  transcriptComponent:
    | CustomEventTranscriptComponent
    | CustomEventPreviewLogProps;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { data } = useGetEventConfigurationsQuery({ draft_mode: 'any' });
  const muiTheme = useTheme();
  const palette = muiTheme.palette.colors;

  const colorSets = {
    dark: {
      background: palette.grey[900],
      border: palette.grey[700],
      eventNameColor: palette.purple[300],
      idColor: palette.blue[300],
      secondaryText: palette.grey[100],
      text: 'white',
    },
    light: {
      background: palette.grey[100],
      border: palette.grey[300],
      eventNameColor: palette.purple[600],
      idColor: palette.blue[600],
      secondaryText: palette.grey[700],
      text: palette.grey[900],
    },
  };
  const colors = colorSets[theme];

  const { event_configuration_id, event_configuration_name, properties } =
    transcriptComponent;

  const displayNameToPropertyValue = useMemo(() => {
    const matchingEvent = data?.find(
      event => event.event_configuration_id === event_configuration_id,
    );

    if (!matchingEvent?.properties.length) {
      return properties;
    }

    // Build an object mapping property labels (display names) to their values
    const displayNameToPropertyValue: Record<string, unknown> = {};

    matchingEvent.properties.forEach(prop => {
      displayNameToPropertyValue[prop.label] = properties[prop.property_id];
    });

    return displayNameToPropertyValue;
  }, [data, event_configuration_id, properties]);

  return (
    <CustomEventContainer
      bgcolor={colors.background}
      border={`1px solid ${colors.border}`}
      colorTheme={theme}
    >
      <Row>
        <IconBolt
          size={20}
          style={{
            color: colors.eventNameColor,
            fill: colors.eventNameColor,
          }}
        />
        <Typography color={colors.text} variant='font14'>
          {t('customEvent.label')}
        </Typography>
        <Typography color={colors.eventNameColor} variant='font14Bold'>
          {event_configuration_name}
        </Typography>
      </Row>

      {Object.keys(properties).length > 0 && (
        <Typography color={colors.secondaryText} variant='font14'>
          <pre
            style={{
              margin: 0,
              maxHeight: '400px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              wordWrap: 'break-word',
            }}
          >
            <span style={{ color: colors.idColor }}>
              {t('customEvent.properties')}
            </span>
            {JSON.stringify(displayNameToPropertyValue, null, 2)}
          </pre>
        </Typography>
      )}
    </CustomEventContainer>
  );
};

const CustomEventContainer = styled(Box)<{
  colorTheme?: 'dark' | 'light';
}>`
  background-color: ${props =>
    props.colorTheme === 'light'
      ? undefined
      : props.theme.palette.colors.grey[900]};
  border: ${props =>
    props.colorTheme === 'light'
      ? undefined
      : `1px solid ${props.theme.palette.colors.grey[700]}`};
  border-radius: 12px;
  font-family: Fira Code !important;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const Row = styled('div')`
  display: flex;
  gap: 8px;
  word-break: break-word;
  align-items: flex-start;
`;
